<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div class="flex items-center gap-4">
                                        <router-link
                                                to="/country"
                                                class="inline-flex items-center gap-2 text-gray-500 no-underline text-sm"
                                        >
                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                </svg>
                                                Back to Countries
                                        </router-link>
                                </div>
                                <div class="flex items-center gap-3">
                                        <button
                                                v-if="hasChanges"
                                                type="button"
                                                class="inline-flex items-center gap-2 bg-orange-7 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50"
                                                :disabled="saving"
                                                @click="saveChanges"
                                        >
                                                <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                {{ saving ? 'Saving...' : 'Save Changes' }}
                                        </button>
                                </div>
                        </div>
                </header>

                <main class="p-6">
                        <div v-if="loading" class="flex justify-center items-center py-16">
                                <div class="text-center">
                                        <div class="w-10 h-10 border-[3px] border-gray-200 border-t-orange-7 rounded-full animate-spin mx-auto"></div>
                                        <p class="mt-4 text-gray-500 text-sm">Loading country...</p>
                                </div>
                        </div>

                        <div v-else-if="country" class="max-w-3xl mx-auto">
                                <div class="flex items-center gap-4 mb-6">
                                        <country-flag v-if="country.iso" :country="country.iso" size="big" />
                                        <div>
                                                <h1 class="text-2xl font-semibold text-gray-900 m-0">{{ country.name || 'New Country' }}</h1>
                                                <p class="text-sm text-gray-500 mt-1 mb-0">Edit country details</p>
                                        </div>
                                </div>

                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200">
                                                <h2 class="text-base font-semibold text-gray-700 m-0">Country Details</h2>
                                        </div>
                                        <div class="p-6">
                                                <div class="grid gap-6">
                                                        <div>
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                                                <input
                                                                        v-model="country.name"
                                                                        type="text"
                                                                        placeholder="Enter country name"
                                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
                                                                >
                                                        </div>
                                                        
                                                        <div class="grid grid-cols-2 gap-4">
                                                                <div>
                                                                        <label class="block text-sm font-medium text-gray-700 mb-2">ISO Code *</label>
                                                                        <input
                                                                                v-model="country.iso"
                                                                                type="text"
                                                                                placeholder="US"
                                                                                maxlength="2"
                                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border uppercase"
                                                                        >
                                                                </div>
                                                                <div>
                                                                        <label class="block text-sm font-medium text-gray-700 mb-2">ISO3 Code</label>
                                                                        <input
                                                                                v-model="country.iso3"
                                                                                type="text"
                                                                                placeholder="USA"
                                                                                maxlength="3"
                                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border uppercase"
                                                                        >
                                                                </div>
                                                        </div>
                                                        
                                                        <div>
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Region</label>
                                                                <div class="flex items-center gap-2">
                                                                        <select
                                                                                v-model="country.regionid"
                                                                                class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
                                                                        >
                                                                                <option :value="null">No region selected</option>
                                                                                <option v-for="region in regions" :key="region.id" :value="region.id">
                                                                                        {{ region.name }}
                                                                                </option>
                                                                        </select>
                                                                        <button
                                                                                v-if="country.regionid !== null"
                                                                                type="button"
                                                                                class="py-2.5 px-2.5 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer"
                                                                                title="Clear region"
                                                                                @click="(country.regionid as number | undefined) = undefined"
                                                                        >
                                                                                <svg class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                                </svg>
                                                                        </button>
                                                                </div>
                                                        </div>
                                                        
                                                        <div>
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Default Currency</label>
                                                                <div class="flex items-center gap-2">
                                                                        <select
                                                                                v-model="country.currency"
                                                                                class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm box-border"
                                                                        >
                                                                                <option :value="null">No currency selected</option>
                                                                                <option v-for="curr in currencies" :key="curr.id" :value="curr.id">
                                                                                        {{ curr.id }} - {{ curr.name }}
                                                                                </option>
                                                                        </select>
                                                                        <button
                                                                                v-if="country.currency !== null"
                                                                                type="button"
                                                                                class="py-2.5 px-2.5 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer"
                                                                                title="Clear currency"
                                                                                @click="(country.currency as string | undefined) = undefined"
                                                                        >
                                                                                <svg class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                                </svg>
                                                                        </button>
                                                                </div>
                                                        </div>
                                                        
                                                        <div class="border-t border-gray-200 pt-6">
                                                                <h3 class="text-sm font-semibold text-gray-700 m-0 mb-4">Settings</h3>
                                                                
                                                                <div class="flex flex-col gap-4">
                                                                        <label class="flex items-center gap-3 cursor-pointer">
                                                                                <input
                                                                                        v-model="country.states"
                                                                                        type="checkbox"
                                                                                        class="w-5 h-5 accent-orange-7"
                                                                                >
                                                                                <div>
                                                                                        <span class="text-sm font-medium text-gray-700">Requires States/Provinces</span>
                                                                                        <p class="text-xs text-gray-500 mt-0.5 mb-0">Does this country need a state or province in the postal address?</p>
                                                                                </div>
                                                                        </label>
                                                                        
                                                                        <label class="flex items-center gap-3 cursor-pointer">
                                                                                <input
                                                                                        v-model="country.delivery"
                                                                                        type="checkbox"
                                                                                        class="w-5 h-5 accent-orange-7"
                                                                                >
                                                                                <div>
                                                                                        <span class="text-sm font-medium text-gray-700">Delivery Available</span>
                                                                                        <p class="text-xs text-gray-500 mt-0.5 mb-0">Can products be delivered to this country?</p>
                                                                                </div>
                                                                        </label>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <div class="hidden mobile-save-button">
                                        <button
                                                v-if="hasChanges"
                                                type="button"
                                                class="w-full inline-flex items-center justify-center gap-2 bg-orange-7 text-white py-3.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer mt-6"
                                                :disabled="saving"
                                                @click="saveChanges"
                                        >
                                                {{ saving ? 'Saving...' : 'Save Changes' }}
                                        </button>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { CountryModel, RegionModel, CurrencyModel } from '@sosocio/creator/interfaces';
import CountryService from 'services/CountryService';
import RegionService from 'services/RegionService';
import CurrencyService from 'services/CurrencyService';
import CountryFlag from 'vue-country-flag-next';
import Swal from 'sweetalert2';

const showToast = (message: string, icon: 'success' | 'error' = 'success') => {
        Swal.fire({
                toast: true,
                position: 'top-end',
                icon,
                title: message,
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                        popup: 'windmill-swal-toast',
                },
        });
};

export default defineComponent({
        name: 'EditCountryPage',
        components: {
                CountryFlag,
        },
        setup() {
                const route = useRoute();
                const router = useRouter();
                
                const country = ref<CountryModel | null>(null);
                const originalCountry = ref<CountryModel | null>(null);
                const regions = ref<RegionModel[]>([]);
                const currencies = ref<CurrencyModel[]>([]);
                const loading = ref(false);
                const saving = ref(false);
                
                const hasChanges = computed(() => {
                        if (!country.value || !originalCountry.value) return false;
                        return JSON.stringify(country.value) !== JSON.stringify(originalCountry.value);
                });
                
                const fetchData = async () => {
                        loading.value = true;
                        try {
                                const countryId = route.params.id as string;
                                const params = new URLSearchParams({ limit: '0' });
                                
                                const [countryData, regionsData, currenciesData] = await Promise.all([
                                        CountryService.get(countryId),
                                        RegionService.getAll(params),
                                        CurrencyService.getAll(params),
                                ]);
                                
                                country.value = { ...countryData };
                                originalCountry.value = { ...countryData };
                                regions.value = regionsData;
                                currencies.value = currenciesData;
                        } catch (error: any) {
                                console.error('Failed to load country:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: 'Failed to load country',
                                        icon: 'error',
                                        customClass: {
                                                popup: 'windmill-swal-popup',
                                                confirmButton: 'windmill-swal-confirm',
                                        },
                                }).then(() => {
                                        router.push('/country');
                                });
                        } finally {
                                loading.value = false;
                        }
                };
                
                const saveChanges = async () => {
                        if (!country.value || !hasChanges.value) return;
                        
                        saving.value = true;
                        try {
                                await CountryService.update(country.value.id, {
                                        name: country.value.name,
                                        iso: country.value.iso.toUpperCase(),
                                        iso3: country.value.iso3?.toUpperCase() || '',
                                        regionid: country.value.regionid,
                                        currency: country.value.currency,
                                        states: country.value.states,
                                        delivery: country.value.delivery,
                                });
                                originalCountry.value = { ...country.value };
                                showToast('Country updated successfully');
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to update country',
                                        icon: 'error',
                                        customClass: {
                                                popup: 'windmill-swal-popup',
                                                confirmButton: 'windmill-swal-confirm',
                                        },
                                });
                        } finally {
                                saving.value = false;
                        }
                };
                
                onMounted(() => {
                        fetchData();
                });
                
                watch(() => route.params.id, () => {
                        fetchData();
                });
                
                return {
                        country,
                        regions,
                        currencies,
                        loading,
                        saving,
                        hasChanges,
                        saveChanges,
                };
        },
});
</script>

<style>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
